// ── BCHNostr — Podcast show page ──────────────────────────────────
//
// The public home for one podcast. Everything on this page is either the
// creator's own signed show event or metadata read from their RSS feed —
// audio is played straight from their host and never proxied.
//
// The show lives on its own page rather than as a giant dashboard bolted
// onto the person's normal Nostr profile: their profile stays a profile.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Mic2, Globe as GlobeIcon, Users, Star, Rocket, RefreshCw, AlertTriangle,
  Calendar, Play, ArrowLeft, Coins, Flame, Clock, TrendingUp, Eye, MousePointerClick,
  BarChart3, Settings2,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useBCHAddress } from '../hooks/useBCHAddress';
import {
  useBCHPodcast, usePodcastFeed, usePodcastFollows, usePodcastFollowerCount,
  usePodcastBoosts,
} from '../hooks/usePodcast';
import {
  podcastCategoryLabel, episodePath, episodeSlug,
  formatEpisodeDuration, formatEpisodeDate,
  recordShowView, summarizeStats, getPodcastStats, FEED_REFRESH_INTERVAL_MS,
  type PodcastShow, type PodcastEpisode, type FeedErrorKind,
} from '../lib/podcast';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { CreatorSupportPanel } from './CreatorSupportPage';
import BCHTipButton from '../components/BCHTipButton';
import PodcastBoostModal from '../components/PodcastBoostModal';
import { BCHLogo } from '../components/Layout';
import UserBadges from '../components/UserBadges';

// ── Feed status notice ────────────────────────────────────────────
//
// A broken feed degrades this page and nothing else: any cached episodes
// keep showing and the reader gets an honest explanation.

const FEED_ERROR_TEXT: Record<FeedErrorKind, string> = {
  'invalid-url': 'This podcast’s feed URL is not valid.',
  'unreachable': 'Couldn’t reach the podcast feed just now.',
  'timeout': 'The podcast feed timed out.',
  'not-a-feed': 'The feed URL didn’t return a podcast feed.',
  'too-large': 'This podcast’s feed is too large to import.',
};

export function FeedNotice({ error, hasEpisodes }: { error: FeedErrorKind; hasEpisodes: boolean }) {
  return (
    <div className="mx-4 my-3 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {FEED_ERROR_TEXT[error]}{' '}
        {hasEpisodes
          ? 'Showing the last episodes we successfully imported.'
          : 'Nothing to show yet — check back later.'}
      </p>
    </div>
  );
}

// ── Episode row ───────────────────────────────────────────────────

export function EpisodeRow({
  show, episode, rank,
}: { show: PodcastShow; episode: PodcastEpisode; rank?: number }) {
  const art = episode.image || show.image;
  const duration = formatEpisodeDuration(episode.duration);
  const date = formatEpisodeDate(episode.publishedAt);

  return (
    <Link
      to={episodePath(show.pubkey, episode.guid)}
      className="flex items-start gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors group"
    >
      {typeof rank === 'number' && (
        <span className={`text-xs font-extrabold w-4 shrink-0 pt-1 text-center ${
          rank === 1 ? 'text-amber-500' : rank === 2 ? 'text-slate-400' : rank === 3 ? 'text-amber-700' : 'text-muted-foreground'
        }`}>
          {rank}
        </span>
      )}
      {art ? (
        <img
          src={art}
          alt=""
          loading="lazy"
          className="w-14 h-14 rounded-lg object-cover border border-border shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Mic2 className="w-5 h-5 text-primary/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          {episode.episodeNumber !== null && (
            <span className="text-muted-foreground font-normal mr-1">#{episode.episodeNumber}</span>
          )}
          {episode.title}
        </p>
        {episode.description && (
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{episode.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
          {date && <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{date}</span>}
          {duration && <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{duration}</span>}
        </div>
      </div>
      <div className="shrink-0 self-center p-2 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Play className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

// ── Follow Podcast button ─────────────────────────────────────────
//
// A podcast subscription, NOT a kind-3 contact-list follow: subscribing
// to a show must not silently rewrite the listener's social graph.

export function FollowPodcastButton({ pubkey, size = 'md' }: { pubkey: string; size?: 'sm' | 'md' }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { isFollowing, toggle, saving, loading } = usePodcastFollows();
  const following = isFollowing(pubkey);
  const isSm = size === 'sm';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { setShowLoginDialog(true); return; }
    toggle(pubkey);
  };

  return (
    <button
      onClick={handleClick}
      disabled={saving || loading}
      className={`no-navigate flex items-center gap-1.5 rounded-full font-semibold transition-colors disabled:opacity-60 ${
        isSm ? 'px-2.5 py-1 text-[11px]' : 'px-3.5 py-2 text-xs'
      } ${
        following
          ? 'bg-primary/15 text-primary hover:bg-primary/25'
          : 'bg-primary text-primary-foreground hover:bg-primary/90'
      }`}
      title={following ? 'Unfollow this podcast' : 'Follow this podcast'}
    >
      <Star className={`${isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} ${following ? 'fill-current' : ''}`} />
      {following ? 'Following' : 'Follow'}
    </button>
  );
}

// ── Owner analytics ───────────────────────────────────────────────
//
// Deliberately modest and deliberately honest. BCHNostr can see page
// views and Play CLICKS inside its own UI. It cannot see downloads from
// the creator's host, and it cannot see how long anyone listened — so it
// never claims to. Counters are per-browser, and labelled as such.

function PodcastAnalytics({ show, followerCount }: { show: PodcastShow; followerCount: number }) {
  const [open, setOpen] = useState(false);
  const stats = useMemo(() => summarizeStats(show.pubkey), [show.pubkey, open]);
  const { boosts } = usePodcastBoosts();
  const myBoosts = boosts.filter(b => b.podcastPubkey === show.pubkey).length;

  return (
    <div className="mx-4 mb-3 rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors"
      >
        <BarChart3 className="w-4 h-4 text-primary shrink-0" />
        <span className="text-xs font-bold flex-1">Podcast Analytics</span>
        <span className="text-[10px] text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-border pt-3">
          <div className="grid grid-cols-2 gap-2">
            <Stat icon={Eye} label="Podcast page views" value={stats.showViews} />
            <Stat icon={Eye} label="Episode page views" value={stats.episodeViews} />
            <Stat icon={MousePointerClick} label="Play clicks" value={stats.playClicks} />
            <Stat icon={Users} label="Followers" value={followerCount} />
            <Stat icon={Rocket} label="Boosts purchased" value={myBoosts} />
          </div>

          {/* The honesty note is not optional — it's the difference between
              a useful signal and a misleading claim. */}
          <div className="rounded-lg bg-muted/50 border border-border p-2.5 space-y-1.5">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Play clicks</strong> count taps on Play inside
              BCHNostr. They are <strong className="text-foreground">not</strong> actual listens:
              BCHNostr cannot see downloads or listening time, because your audio is served
              directly by your own host.
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              For real download and listen numbers, use the stats from your podcast host.
              View and click counts here are recorded in this browser only.
            </p>
          </div>

          <Link
            to="/podcasts"
            className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-muted text-xs font-medium hover:bg-muted/70 transition-colors"
          >
            <Settings2 className="w-3.5 h-3.5" /> Edit podcast settings
          </Link>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-background p-2.5">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[10px] text-muted-foreground leading-tight">{label}</span>
      </div>
      <p className="text-lg font-bold tabular-nums mt-0.5">{value.toLocaleString()}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────

export default function PodcastPage() {
  const { pubkey: rawPubkey } = useParams<{ pubkey: string }>();
  const pubkey = useMemo(
    () => (rawPubkey && /^[0-9a-f]{64}$/i.test(rawPubkey) ? rawPubkey.toLowerCase() : undefined),
    [rawPubkey],
  );

  const { user } = useAppContext();
  const { podcast, loading: showLoading } = useBCHPodcast(pubkey);
  const { profile } = useNostrProfile(pubkey);
  const { episodes, loading: feedLoading, error, refreshing, refresh, updatedAt } =
    usePodcastFeed(podcast?.feedUrl);
  const { count: followerCount } = usePodcastFollowerCount(pubkey);
  const { address: creatorAddress } = useBCHAddress(pubkey);
  const { price: bchPrice } = useBCHPrice();
  const [tab, setTab] = useState<'latest' | 'popular' | 'support'>('latest');
  const [boostOpen, setBoostOpen] = useState(false);

  const isOwner = !!user && user.pubkey === pubkey;

  // Count this visit once per mount, for the creator's own local counters.
  useEffect(() => {
    if (pubkey && podcast) recordShowView(pubkey);
  }, [pubkey, podcast]);

  // "Popular" — BCHNostr can't rank by real play counts (only the host
  // knows those), so we rank by this browser's recorded play clicks and
  // fall back to recency. Labelled honestly above the list.
  const popular = useMemo(() => {
    if (!pubkey || episodes.length === 0) return episodes;
    const clicks = getPodcastStats().playClicks;
    return [...episodes].sort((a, b) => {
      const aClicks = clicks[`${pubkey}:${episodeSlug(a.guid)}`] || 0;
      const bClicks = clicks[`${pubkey}:${episodeSlug(b.guid)}`] || 0;
      if (aClicks !== bClicks) return bClicks - aClicks;
      return b.publishedAt - a.publishedAt;
    });
  }, [episodes, pubkey]);

  if (!pubkey) {
    return <EmptyState title="Podcast not found" body="That podcast link isn’t valid." />;
  }

  if (showLoading && !podcast) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!podcast) {
    return (
      <EmptyState
        title="No podcast here"
        body={
          isOwner
            ? 'Connect your RSS feed on the BCH Podcasts page and your podcast page will build itself.'
            : 'This account hasn’t connected a podcast.'
        }
        action={{ to: '/podcasts', label: isOwner ? 'Connect your podcast' : 'Browse podcasts' }}
      />
    );
  }

  const displayName = profile?.display_name || profile?.name || 'Podcaster';
  const cover = podcast.image;
  const tipAddress = podcast.bch || creatorAddress || '';
  const list = tab === 'popular' ? popular : episodes;

  return (
    <div className="pb-6">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-2">
        <Link to="/podcasts" className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted text-muted-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="text-base font-bold truncate flex-1">🎙 {podcast.name}</h1>
        <button
          onClick={refresh}
          disabled={refreshing}
          title="Refresh episodes"
          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── Show identity ── */}
      <div className="p-4">
        <div className="flex items-start gap-3.5">
          {cover ? (
            <img
              src={cover}
              alt=""
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover border border-border shrink-0"
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Mic2 className="w-9 h-9 text-primary/50" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-extrabold leading-tight">{podcast.name}</h2>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
              <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                {podcastCategoryLabel(podcast.category)}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-2.5 h-2.5" />
                {followerCount.toLocaleString()} follower{followerCount === 1 ? '' : 's'}
              </span>
            </div>
            {podcast.hosts.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-1 truncate">
                Hosted by <span className="text-foreground font-medium">{podcast.hosts.join(', ')}</span>
              </p>
            )}
            <Link
              to={`/profile/${podcast.pubkey}`}
              className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] text-primary hover:underline"
            >
              {profile?.picture && (
                <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
              )}
              <span className="truncate max-w-[10rem]">{displayName}</span>
              <UserBadges pubkey={podcast.pubkey} size="sm" showTooltip={false} />
            </Link>
          </div>
        </div>

        {podcast.description && (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-3 leading-relaxed">
            {podcast.description}
          </p>
        )}

        {/* ── Actions ── */}
        <div className="flex flex-wrap items-center gap-2 mt-3.5">
          <FollowPodcastButton pubkey={podcast.pubkey} />

          {tipAddress && !isOwner && (
            <div className="no-navigate">
              <BCHTipButton recipientPubkey={podcast.pubkey} recipientProfile={profile} />
            </div>
          )}

          <button
            onClick={() => setBoostOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-semibold hover:opacity-90"
          >
            <Rocket className="w-3.5 h-3.5" /> Promote
          </button>

          {podcast.website && (
            <a
              href={podcast.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
            >
              <GlobeIcon className="w-3.5 h-3.5" /> Website
            </a>
          )}
        </div>

        {/* Optional BCH support line — the show's own donation address */}
        {podcast.bch && (
          <div className="flex items-center gap-1.5 mt-3 text-[11px] text-muted-foreground">
            <BCHLogo size={12} />
            <span>Supports Bitcoin Cash donations</span>
          </div>
        )}
      </div>

      {isOwner && <PodcastAnalytics show={podcast} followerCount={followerCount} />}

      {/* ── Tabs ── */}
      <div className="flex border-b border-border sticky top-[3.25rem] z-[9] bg-background/90 backdrop-blur-md">
        {([
          { id: 'latest', label: 'Latest Episodes', icon: Calendar },
          { id: 'popular', label: 'Popular', icon: Flame },
          { id: 'support', label: 'Support', icon: Coins },
        ] as const).map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {error && <FeedNotice error={error} hasEpisodes={episodes.length > 0} />}

      {tab === 'support' ? (
        <div className="p-4">
          {/* Reuses the existing creator economy wholesale — membership,
              goals, and crowdfunding. No separate podcast payment system. */}
          <CreatorSupportPanel pubkey={podcast.pubkey} bchPrice={bchPrice} />
        </div>
      ) : feedLoading && episodes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Importing episodes…</p>
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 px-4">
          <Mic2 className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">No episodes in this feed yet.</p>
        </div>
      ) : (
        <>
          {tab === 'popular' && (
            <p className="px-4 py-2 text-[10px] text-muted-foreground border-b border-border">
              Ranked by play clicks recorded in this browser, then by date. BCHNostr can’t
              measure real listens — only the podcast’s host can.
            </p>
          )}
          {list.slice(0, 50).map((ep, i) => (
            <EpisodeRow
              key={ep.guid}
              show={podcast}
              episode={ep}
              rank={tab === 'popular' ? i + 1 : undefined}
            />
          ))}
          <p className="px-4 pt-3 text-[10px] text-muted-foreground/70 flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" />
            Episodes come from the podcast’s RSS feed
            {updatedAt > 0 && ` · updated ${new Date(updatedAt).toLocaleTimeString()}`}
            {` · refreshed at most every ${Math.round(FEED_REFRESH_INTERVAL_MS / 60000)} min`}
          </p>
        </>
      )}

      {boostOpen && (
        <PodcastBoostModal
          podcastPubkey={podcast.pubkey}
          podcastName={podcast.name}
          onClose={() => setBoostOpen(false)}
        />
      )}
    </div>
  );
}

// ── Shared empty state ────────────────────────────────────────────

export function EmptyState({ title, body, action }: {
  title: string;
  body: string;
  action?: { to: string; label: string };
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-6 text-center">
      <Mic2 className="w-14 h-14 text-muted-foreground/25" />
      <h2 className="text-lg font-bold">{title}</h2>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">{body}</p>
      {action && (
        <Link
          to={action.to}
          className="mt-1 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90"
        >
          {action.label}
        </Link>
      )}
    </div>
  );
}
